import { useMemo } from 'react';
import {
  SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS,
  SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53
} from '@portal/config/base';
import { StoreCode } from 'schema/storeCode';
import { getIdFromShopifyFormattedId } from 'shared/utils/shopify/ids';
import { useHasPouchOrPackSubscription } from '@portal/processes/ShopListProducts/hooks/useHasPouchOrPackSubscription';

export type ProductType = 'pouch' | 'travelPack' | 'agz' | 'pouchOTP' | 'travelPackOTP' | 'omega';

export const normalizeVariantId = (variantId: string): string => {
  if (variantId.startsWith('gid://shopify/')) {
    // Recursively extract the ID from the nested prefix
    return normalizeVariantId(getIdFromShopifyFormattedId(variantId));
  }

  // Handle variantId-quantity format (e.g., "41897472196651-1")
  if (variantId.includes('-')) {
    return variantId.split('-')[0];
  }

  return variantId;
};

/**
 * Checks if a variant ID matches any of the AGZ variants
 */
export const isAgzProduct = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);
  const agzFlavors = SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS[store];

  if (!agzFlavors) {
    return false;
  }

  return Object.values(agzFlavors).some(
    (flavorVariants) =>
      flavorVariants.default === normalizedId ||
      flavorVariants.subscriptionPrice === normalizedId ||
      flavorVariants.bundlePrice === normalizedId
  );
};

/**
 * Checks if a variant ID matches any of the Omega variants
 */
export const isOmegaProduct = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);
  const omegaVariants = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store];

  if (!omegaVariants) {
    return false;
  }

  return (
    normalizedId === omegaVariants.default ||
    normalizedId === omegaVariants.subscriptionPrice ||
    normalizedId === omegaVariants.bundlePrice
  );
};

/**
 * Helper function to check if a variant ID matches any values in a VariantIdObject
 */
const matchesVariantIdObject = (normalizedId: string, variantObj: any): boolean => {
  if (!variantObj) return false;

  return Object.values(variantObj).some((value) => value === normalizedId);
};

/**
 * Checks if a variant ID matches any of the Pouch subscription variants
 */
export const isPouchProduct = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);

  // Check V53 pouch subscription variants
  const pouchVariants = [
    SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store],
    SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store],
    SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store]
  ];

  return pouchVariants.some((variantObj) => matchesVariantIdObject(normalizedId, variantObj));
};

/**
 * Checks if a variant ID matches any of the Travel Pack subscription variants
 */
export const isPackProduct = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);

  // Check V53 travel pack subscription variants
  const packVariants = [
    SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store],
    SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store],
    SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store]
  ];

  return packVariants.some((variantObj) => matchesVariantIdObject(normalizedId, variantObj));
};

export const useSpecialMemberPricing = (variantId: string, store: StoreCode) => {
  const isProductAgz = useMemo(() => isAgzProduct(variantId, store), [variantId, store]);
  const isProductOmega = useMemo(() => isOmegaProduct(variantId, store), [variantId, store]);
  const isProductPouch = useMemo(() => isPouchProduct(variantId, store), [variantId, store]);
  const isProductPack = useMemo(() => isPackProduct(variantId, store), [variantId, store]);
  const { hasAnyActiveSubWithPouchOrPackSubscription, isPending: isSubscriptionLoading } =
    useHasPouchOrPackSubscription();

  return {
    isAgzProduct: isProductAgz,
    isOmegaProduct: isProductOmega,
    isPouchProduct: isProductPouch,
    isPackProduct: isProductPack,
    omegaVariants: SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store],
    agzVariants: SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS[store],
    showMemberPrice: hasAnyActiveSubWithPouchOrPackSubscription,
    isSubscriptionLoading
  };
};
